/**
 * Projects API Service
 *
 * Client-side API functions for kitchen project management.
 */

import api from "./api";

export interface Project {
  id: string;
  name: string;
  description: string | null;
  measurements: string | null;
  selections: string | null;
  raumplanData: string | null;
  status: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListResponse {
  items: Project[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  measurements?: string;
  selections?: string;
  raumplanData?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  measurements?: string;
  selections?: string;
  raumplanData?: string;
  status?: "draft" | "planning" | "review" | "approved" | "archived";
}

/**
 * List projects for the current user
 */
export async function listProjects(params: {
  status?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<ProjectListResponse> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  const response = await api.get<ProjectListResponse>(`/api/projects${query ? `?${query}` : ""}`);
  return response.data;
}

/**
 * Get a single project by ID
 */
export async function getProject(id: string): Promise<Project> {
  const response = await api.get<{ data: Project }>(`/api/projects/${id}`);
  return response.data.data;
}

/**
 * Create a new project
 */
export async function createProject(data: CreateProjectRequest): Promise<Project> {
  const response = await api.post<{ data: Project }>("/api/projects", data);
  return response.data.data;
}

/**
 * Update a project
 */
export async function updateProject(id: string, data: UpdateProjectRequest): Promise<Project> {
  const response = await api.put<{ data: Project }>(`/api/projects/${id}`, data);
  return response.data.data;
}

/**
 * Delete a project
 */
export async function deleteProject(id: string): Promise<void> {
  await api.delete(`/api/projects/${id}`);
}

/**
 * Get or create a default project for the current session
 * Used when starting a new design without explicitly saving first
 */
export async function getOrCreateDefaultProject(floorPlanData?: {
  room?: { width: number; height: number };
  volumes?: unknown[];
  walls?: unknown[];
}): Promise<Project> {
  // Try to get the most recent draft project
  const result = await listProjects({ status: "draft", limit: 1 });

  if (result.items.length > 0) {
    return result.items[0];
  }

  // Create a new project
  const measurements = floorPlanData?.room
    ? JSON.stringify({ width: floorPlanData.room.width, height: floorPlanData.room.height })
    : undefined;

  const raumplanData = floorPlanData
    ? JSON.stringify({
        volumes: floorPlanData.volumes || [],
        walls: floorPlanData.walls || [],
      })
    : undefined;

  return createProject({
    name: `Kitchen Design - ${new Date().toLocaleDateString()}`,
    measurements,
    raumplanData,
  });
}
